import { useEffect, useMemo, useRef, useState } from "react";
import {
  CameraIcon,
  ChatBubbleLeftEllipsisIcon,
  ChevronDownIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  PencilSquareIcon,
  PhotoIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { EnhancedChecklistSelector } from "../service/reports/enhanced-checklist-selector";
import {
  DEFAULT_DEVIATION_MARKER_TYPE,
  DEVIATION_MARKER_TYPES,
  MARKUP_COLORS,
} from "./drawingViewerConstants";
import {
  CHECKLIST_PIN_COLOR,
  directlyPinnedChecklistIds,
  OBSERVATION_CATEGORIES,
  OBSERVATION_SEVERITIES,
  OBSERVATION_TYPES,
} from "./drawingObservationUtils";
import { deviationConsequenceOptions } from "../quality/deviations/utils";

const MarkerShapePreview = ({ type, color = "#e5484d", active }) => {
  const stroke = active ? "#fff" : color;
  if (type === "arrow") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 2L2 12L12 22M2 12H22"
          stroke={stroke}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ transform: "rotate(-45deg)", transformOrigin: "center" }}
        />
      </svg>
    );
  }
  if (type === "rectangular") {
    return (
      <span
        className="block h-3.5 w-5 rounded-[2px] border-2"
        style={{
          borderColor: stroke,
          background: active ? "rgba(255,255,255,0.25)" : "transparent",
        }}
      />
    );
  }
  return (
    <span
      className="block h-4 w-4 rounded-full border-2"
      style={{
        borderColor: stroke,
        background: active ? "rgba(255,255,255,0.25)" : "transparent",
      }}
    />
  );
};

const TYPE_ICONS = {
  photo: CameraIcon,
  deviation: ExclamationTriangleIcon,
  note: ChatBubbleLeftEllipsisIcon,
  checklist: ClipboardDocumentCheckIcon,
};

const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
      {label}
      {hint && (
        <span className="text-[10px] font-medium normal-case tracking-normal text-[#969da8]">
          {hint}
        </span>
      )}
    </span>
    {children}
  </label>
);

const inputClassName =
  "w-full rounded-[7px] border-[1.5px] border-[#d6dae0] bg-white px-2.5 py-2 text-[13.5px] text-[#14171c] outline-none transition-[border-color,box-shadow] focus:border-[#2b6ef0] focus:shadow-[0_0_0_3px_#eaf1fe]";

const modePillClass = (active) =>
  `flex-1 rounded-[9px] border-[1.5px] px-2.5 py-2 text-[12.5px] font-semibold transition-colors ${
    active
      ? "border-[#2b6ef0] bg-[#eaf1fe] text-[#1d4fb0] shadow-[0_0_0_1px_#2b6ef0]"
      : "border-[#e5e8ec] bg-white text-[#59616d] hover:border-[#d6dae0]"
  }`;

export const DrawingAddObservationDialog = ({
  t,
  open,
  onClose,
  onPlace,
  page,
  drawingTitle,
  drawing = null,
  checklists = [],
  projectId,
  modules = [],
  initialKind,
  initialChecklistId,
  initialChecklistMode,
}) => {
  const [kind, setKind] = useState("photo");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [consequence, setConsequence] = useState("");
  const [consequenceType, setConsequenceType] = useState("");
  const [category, setCategory] = useState(OBSERVATION_CATEGORIES[0]);
  const [severity, setSeverity] = useState("major");
  const [markerType, setMarkerType] = useState(DEFAULT_DEVIATION_MARKER_TYPE);
  const [noteColor, setNoteColor] = useState(MARKUP_COLORS[4].val);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [wantSketch, setWantSketch] = useState(false);
  const [checklistMode, setChecklistMode] = useState("new");
  const [typeSelectorOpen, setTypeSelectorOpen] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedTypeTitle, setSelectedTypeTitle] = useState("");
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [checklistTitle, setChecklistTitle] = useState("");
  const [selectedChecklistId, setSelectedChecklistId] = useState("");
  const [checklistSearch, setChecklistSearch] = useState("");
  const [checklistHouse, setChecklistHouse] = useState("");
  const [checklistApartment, setChecklistApartment] = useState("");
  const firstRef = useRef(null);
  const bodyRef = useRef(null);

  const pinnedChecklistIds = useMemo(
    () => directlyPinnedChecklistIds(modules),
    [modules]
  );

  const availableChecklists = useMemo(
    () =>
      (checklists || []).filter(
        (item) => !pinnedChecklistIds.has(Number(item.id))
      ),
    [checklists, pinnedChecklistIds]
  );

  const filteredChecklists = useMemo(() => {
    const q = checklistSearch.trim().toLowerCase();
    if (!q) return availableChecklists;
    return availableChecklists.filter((item) => {
      const label =
        `${item.displayTitle || ""} ${item.title || ""} ${item.number || ""}`.toLowerCase();
      return label.includes(q);
    });
  }, [availableChecklists, checklistSearch]);

  useEffect(() => {
    if (!open) return;
    const seededKind = initialKind || "photo";
    const seededMode =
      initialChecklistMode || (initialChecklistId != null ? "link" : "new");
    setKind(seededKind);
    setTitle("");
    setDescription("");
    setConsequence("");
    setConsequenceType("");
    setCategory(OBSERVATION_CATEGORIES[0]);
    setSeverity("major");
    setMarkerType(DEFAULT_DEVIATION_MARKER_TYPE);
    setNoteColor(MARKUP_COLORS[4].val);
    setPhotoFile(null);
    setPhotoPreview(null);
    setCaption("");
    setDragOver(false);
    setWantSketch(false);
    setChecklistMode(seededMode);
    setTypeSelectorOpen(false);
    setSelectedTypeId("");
    setSelectedTypeTitle("");
    setSelectedItemIds([]);
    setChecklistTitle("");
    setSelectedChecklistId(
      initialChecklistId != null ? String(initialChecklistId) : ""
    );
    setChecklistSearch("");
    setChecklistHouse(
      drawing?.house ||
        drawing?.drawing_floor?.drawing_house?.name ||
        ""
    );
    setChecklistApartment("");
    requestAnimationFrame(() => {
      bodyRef.current?.scrollTo?.({ top: 0 });
    });
  }, [open, initialKind, initialChecklistId, initialChecklistMode, drawing]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => firstRef.current?.focus(), 60);
    return () => clearTimeout(timer);
  }, [open, kind, checklistMode]);

  useEffect(() => {
    return () => {
      if (photoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  const readFile = (file) => {
    if (!file || !/^image\//.test(file.type)) return;
    if (photoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const valid =
    kind === "photo"
      ? !!photoFile
      : kind === "note"
        ? title.trim().length > 0
        : kind === "checklist"
          ? checklistMode === "new"
            ? !!selectedTypeId && !!projectId
            : (selectedChecklistId &&
              availableChecklists.some(
                (item) => String(item.id) === String(selectedChecklistId)
              ))
          : title.trim().length > 0;

  const buildPayload = () => {
    const base = { kind, page, wantSketch: kind === "photo" || kind === "deviation" ? wantSketch : false };
    if (kind === "deviation") {
      const sev = OBSERVATION_SEVERITIES.find((item) => item.id === severity);
      const moduleType = DEVIATION_MARKER_TYPES.includes(markerType)
        ? markerType
        : DEFAULT_DEVIATION_MARKER_TYPE;
      return {
        ...base,
        title: title.trim(),
        description: description.trim(),
        consequence: consequence.trim(),
        consequence_type: consequenceType ? Number(consequenceType) : null,
        category,
        severity,
        severityApiValue: sev?.apiValue || "2",
        color: sev?.color || "#f4690f",
        module_type: moduleType,
      };
    }
    if (kind === "photo") {
      const linked = (checklists || []).find(
        (item) => String(item.id) === String(selectedChecklistId)
      );
      return {
        ...base,
        title: caption.trim() || t("drawings.observation.photo_default_title"),
        caption: caption.trim(),
        photoFile,
        color: "#2ea66b",
        checklist_id: linked ? Number(linked.id) : null,
        checklist_title: linked
          ? linked.displayTitle || linked.title || null
          : null,
      };
    }
    if (kind === "checklist") {
      if (checklistMode === "new") {
        const resolvedTitle =
          checklistTitle.trim() ||
          selectedTypeTitle ||
          t("drawings.observation.type.checklist");
        return {
          ...base,
          createFromType: true,
          checklist_type_id: Number(selectedTypeId),
          selected_item_ids: selectedItemIds,
          title: resolvedTitle,
          house: checklistHouse.trim() || undefined,
          apartment: checklistApartment.trim() || undefined,
          color: CHECKLIST_PIN_COLOR,
        };
      }
      const linkedChecklist = availableChecklists.find(
        (item) => String(item.id) === String(selectedChecklistId)
      );
      if (!linkedChecklist) {
        return null;
      }
      return {
        ...base,
        createFromType: false,
        checklist_id: Number(linkedChecklist.id),
        title:
          linkedChecklist?.displayTitle ||
          linkedChecklist?.title ||
          t("drawings.observation.type.checklist"),
        color: CHECKLIST_PIN_COLOR,
      };
    }
    return {
      ...base,
      title: title.trim(),
      color: noteColor,
    };
  };

  const handleSubmit = () => {
    if (!valid) return;
    const payload = buildPayload();
    if (!payload) return;
    onPlace(payload);
  };

  const activeType =
    OBSERVATION_TYPES.find((item) => item.kind === kind) || OBSERVATION_TYPES[0];
  const ActiveIcon = TYPE_ICONS[kind] || CameraIcon;

  let form;
  if (kind === "deviation") {
    form = (
      <>
        <Field label={t("drawings.observation.field.title")}>
          <input
            ref={firstRef}
            className={inputClassName}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("drawings.observation.deviation_title_placeholder")}
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t("drawings.observation.field.category")}>
            <div className="relative">
              <select
                className={`${inputClassName} appearance-none pr-8`}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {OBSERVATION_CATEGORIES.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[#59616d]" />
            </div>
          </Field>
          <Field label={t("drawings.observation.field.severity")}>
            <div className="grid grid-cols-3 gap-1.5">
              {OBSERVATION_SEVERITIES.map((item) => {
                const active = severity === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSeverity(item.id)}
                    className={`flex h-9 items-center justify-center gap-1.5 rounded-[7px] border-[1.5px] text-[12.5px] font-semibold transition-colors ${
                      active
                        ? "border-transparent text-white"
                        : "border-[#d6dae0] bg-white text-[#59616d] hover:border-[#969da8]"
                    }`}
                    style={
                      active
                        ? { background: item.color, borderColor: item.color }
                        : undefined
                    }
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ background: active ? "#fff" : item.color }}
                    />
                    {t(item.labelKey)}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>
        <Field label={t("drawings.observation.field.marker_shape")}>
          <div className="grid grid-cols-3 gap-1.5">
            {DEVIATION_MARKER_TYPES.map((type) => {
              const active = markerType === type;
              const sevColor =
                OBSERVATION_SEVERITIES.find((item) => item.id === severity)
                  ?.color || "#e5484d";
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setMarkerType(type)}
                  className={`flex h-10 items-center justify-center gap-1.5 rounded-[7px] border-[1.5px] text-[12.5px] font-semibold transition-colors ${
                    active
                      ? "border-transparent text-white"
                      : "border-[#d6dae0] bg-white text-[#59616d] hover:border-[#969da8]"
                  }`}
                  style={
                    active
                      ? { background: sevColor, borderColor: sevColor }
                      : undefined
                  }
                >
                  <MarkerShapePreview type={type} color={sevColor} active={active} />
                  {t(`drawings.observation.marker.${type}`)}
                </button>
              );
            })}
          </div>
        </Field>
        <Field
          label={t("drawings.observation.field.explanation")}
          hint={t("drawings.observation.optional")}
        >
          <textarea
            rows={2}
            className={inputClassName}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("drawings.observation.deviation_explanation_placeholder")}
          />
        </Field>
        <Field
          label={t("drawings.observation.field.consequence_type")}
          hint={t("drawings.observation.optional")}
        >
          <div className="relative">
            <select
              className={`${inputClassName} appearance-none pr-8`}
              value={consequenceType}
              onChange={(e) => setConsequenceType(e.target.value)}
            >
              <option value="">
                {t("drawings.observation.consequence_type_placeholder")}
              </option>
              {deviationConsequenceOptions.map((opt) => (
                <option key={opt.id} value={opt.value}>
                  {t(`drawings.observation.consequence_type.${opt.label}`, {
                    defaultValue: opt.title,
                  })}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[#59616d]" />
          </div>
        </Field>
        <Field
          label={t("drawings.observation.field.consequence")}
          hint={t("drawings.observation.optional")}
        >
          <textarea
            rows={2}
            className={inputClassName}
            value={consequence}
            onChange={(e) => setConsequence(e.target.value)}
            placeholder={t("drawings.observation.deviation_consequence_placeholder")}
          />
        </Field>
      </>
    );
  } else if (kind === "photo") {
    form = (
      <>
        <Field
          label={t("drawings.observation.field.caption")}
          hint={t("drawings.observation.optional")}
        >
          <input
            ref={firstRef}
            className={inputClassName}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={t("drawings.observation.photo_caption_placeholder")}
          />
        </Field>
        <Field label={t("drawings.observation.field.photo")}>
          <label
            className={`relative flex min-h-[96px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[10px] border-[1.5px] border-dashed p-3 text-center transition-colors ${
              dragOver
                ? "border-[#2b6ef0] bg-[#eaf1fe]"
                : "border-[#d6dae0] bg-[#f6f7f9] hover:border-[#969da8]"
            } ${photoPreview ? "overflow-hidden border-solid p-0" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              readFile(e.dataTransfer.files?.[0]);
            }}
          >
            {photoPreview ? (
              <>
                <img
                  src={photoPreview}
                  alt=""
                  className="mx-auto block max-h-[140px] w-auto max-w-full object-contain"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (photoPreview?.startsWith("blob:")) {
                      URL.revokeObjectURL(photoPreview);
                    }
                    setPhotoFile(null);
                    setPhotoPreview(null);
                  }}
                  className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-[7px] bg-[rgba(20,23,28,0.66)] text-white backdrop-blur-sm"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#eef0f3] text-[#59616d]">
                  <PhotoIcon className="h-6 w-6" />
                </span>
                <span className="text-[13px] text-[#59616d]">
                  {t("drawings.observation.photo_drop")}
                </span>
                <span className="font-mono text-[11px] text-[#969da8]">
                  {t("drawings.observation.photo_drop_hint")}
                </span>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => readFile(e.target.files?.[0])}
            />
          </label>
        </Field>
        {(checklists || []).length > 0 && (
          <Field
            label={t("drawings.observation.field.link_checklist")}
            hint={t("drawings.observation.optional")}
          >
            <select
              className={inputClassName}
              value={selectedChecklistId}
              onChange={(e) => setSelectedChecklistId(e.target.value)}
            >
              <option value="">
                {t("drawings.observation.checklist_none")}
              </option>
              {(checklists || []).map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.displayTitle || item.title || `#${item.id}`}
                </option>
              ))}
            </select>
          </Field>
        )}
      </>
    );
  } else if (kind === "checklist") {
    form = (
      <>
        <div className="rounded-[10px] border border-[#bfdcf7] bg-[#eaf3fd] px-3 py-2.5 text-[12.5px] leading-snug text-[#0b3a78]">
          {t("drawings.observation.checklist_project_copy")}
        </div>
        {!projectId && (
          <div className="rounded-[10px] border border-[#fcd34d] bg-[#fffbeb] px-3 py-2 text-[12.5px] text-[#92400e]">
            {t("drawings.observation.checklist_project_required")}
          </div>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            className={modePillClass(checklistMode === "new")}
            onClick={() => setChecklistMode("new")}
          >
            {t("drawings.observation.checklist_mode.new")}
          </button>
          <button
            type="button"
            className={modePillClass(checklistMode === "link")}
            onClick={() => setChecklistMode("link")}
          >
            {t("drawings.observation.checklist_mode.link")}
          </button>
        </div>

        {checklistMode === "new" ? (
          <>
            <Field label={t("drawings.observation.field.checklist_type")}>
              <button
                ref={firstRef}
                type="button"
                disabled={!projectId}
                onClick={() => setTypeSelectorOpen(true)}
                className={`${inputClassName} flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60`}
              >
                <span className="min-w-0 flex-1">
                  {selectedTypeId ? (
                    <>
                      <span className="block truncate font-semibold text-[#14171c]">
                        {selectedTypeTitle ||
                          t("drawings.observation.checklist_type_placeholder")}
                      </span>
                      {selectedItemIds.length > 0 && (
                        <span className="mt-0.5 block text-[11px] font-medium text-[#59616d]">
                          {t("drawings.observation.checklist_items_selected", {
                            count: selectedItemIds.length,
                          })}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-[#969da8]">
                      {t("drawings.observation.checklist_type_placeholder")}
                    </span>
                  )}
                </span>
                <ChevronDownIcon className="h-4 w-4 flex-none text-[#59616d]" />
              </button>
            </Field>
            {selectedTypeId ? (
              <>
                <Field
                  label={t("drawings.observation.field.title")}
                  hint={t("drawings.observation.optional")}
                >
                  <input
                    className={inputClassName}
                    value={checklistTitle}
                    onChange={(e) => setChecklistTitle(e.target.value)}
                    placeholder={t(
                      "drawings.observation.checklist_title_placeholder"
                    )}
                  />
                </Field>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field
                    label={t("drawings.observation.field.house")}
                    hint={t("drawings.observation.optional")}
                  >
                    <input
                      className={inputClassName}
                      value={checklistHouse}
                      onChange={(e) => setChecklistHouse(e.target.value)}
                      placeholder={t(
                        "drawings.observation.checklist_house_placeholder"
                      )}
                    />
                  </Field>
                  <Field
                    label={t("drawings.observation.field.apartment")}
                    hint={t("drawings.observation.optional")}
                  >
                    <input
                      className={inputClassName}
                      value={checklistApartment}
                      onChange={(e) => setChecklistApartment(e.target.value)}
                      placeholder={t(
                        "drawings.observation.checklist_apartment_placeholder"
                      )}
                    />
                  </Field>
                </div>
              </>
            ) : null}
          </>
        ) : (
          <>
            <Field label={t("drawings.observation.field.search")}>
              <input
                ref={firstRef}
                className={inputClassName}
                value={checklistSearch}
                onChange={(e) => setChecklistSearch(e.target.value)}
                placeholder={t("drawings.observation.checklist_search_placeholder")}
              />
            </Field>
            <Field label={t("drawings.observation.field.existing_checklist")}>
              <div className="max-h-[160px] space-y-1 overflow-y-auto rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9] p-1.5">
                {filteredChecklists.length === 0 ? (
                  <div className="px-2 py-3 text-center text-[12.5px] text-[#969da8]">
                    {t("drawings.observation.checklist_link_empty")}
                  </div>
                ) : (
                  filteredChecklists.map((item) => {
                    const active =
                      String(selectedChecklistId) === String(item.id);
                    const label =
                      item.displayTitle || item.title || `#${item.id}`;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setSelectedChecklistId(String(item.id))}
                        className={`flex w-full items-center gap-2 rounded-[8px] border px-2.5 py-2 text-left text-[13px] transition-colors ${
                          active
                            ? "border-[#2b6ef0] bg-white text-[#14171c] shadow-[0_0_0_1px_#2b6ef0]"
                            : "border-transparent bg-white text-[#334155] hover:border-[#d6dae0]"
                        }`}
                      >
                        <ClipboardDocumentCheckIcon
                          className="h-4 w-4 shrink-0"
                          style={{ color: CHECKLIST_PIN_COLOR }}
                        />
                        <span className="min-w-0">
                          <span className="block truncate font-semibold">
                            {label}
                          </span>
                          {item.number && (
                            <span className="block font-mono text-[11px] text-[#969da8]">
                              {item.number}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </Field>
          </>
        )}
      </>
    );
  } else {
    form = (
      <>
        <Field label={t("drawings.observation.field.note")}>
          <textarea
            ref={firstRef}
            rows={3}
            className={inputClassName}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("drawings.observation.note_placeholder")}
          />
        </Field>
        <Field label={t("drawings.observation.field.pin_color")}>
          <div className="flex gap-1.5 pt-0.5">
            {MARKUP_COLORS.map((swatch) => (
              <button
                key={swatch.id}
                type="button"
                onClick={() => setNoteColor(swatch.val)}
                className={`h-6 w-6 rounded-full border-2 border-white transition-transform hover:scale-110 ${
                  noteColor === swatch.val
                    ? "shadow-[0_0_0_2px_#fff,0_0_0_4px_#2b6ef0]"
                    : "shadow-[0_0_0_1px_#d6dae0]"
                }`}
                style={{ background: swatch.val }}
              />
            ))}
          </div>
        </Field>
      </>
    );
  }

  const showSketchToggle = kind === "photo" || kind === "deviation";

  return (
    <>
    <PMDialog
      open={open}
      onClose={onClose}
      size="md"
      zIndex={120}
      panelClassName="!p-0 sm:!p-0"
    >
      <div className="flex h-full max-h-[100dvh] flex-col overflow-hidden sm:max-h-[min(92dvh,720px)]">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#e5e8ec] px-3 py-2 sm:px-4 sm:py-3.5">
          <div className="flex min-w-0 items-center gap-2 text-[14px] font-semibold tracking-[-0.01em] text-[#14171c] sm:gap-2.5 sm:text-[15px]">
            <span
              className="grid h-7 w-7 flex-none place-items-center rounded-lg text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]"
              style={{ background: activeType.tint }}
            >
              <ActiveIcon className="h-4 w-4" />
            </span>
            <span className="truncate">{t("drawings.observation.dialog_title")}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-md text-[#59616d] hover:bg-[#eef0f3]"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Compact type chips on mobile; 2×2 cards on sm+ (md dialog is too narrow for 4 cols) */}
        <div className="flex-shrink-0 border-b border-[#e5e8ec] px-3 pb-2 pt-2 sm:px-4 sm:pb-3 sm:pt-3.5">
          <div className="flex gap-1.5 overflow-x-auto pb-0.5 sm:hidden">
            {OBSERVATION_TYPES.map((type) => {
              const Icon = TYPE_ICONS[type.kind];
              const active = kind === type.kind;
              return (
                <button
                  key={type.kind}
                  type="button"
                  onClick={() => setKind(type.kind)}
                  className={`inline-flex h-8 flex-none items-center gap-1 rounded-full border px-2.5 text-[12px] font-semibold ${
                    active
                      ? "border-transparent text-white"
                      : "border-[#e5e8ec] bg-white text-[#59616d]"
                  }`}
                  style={active ? { background: type.tint } : undefined}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t(type.labelKey)}
                </button>
              );
            })}
          </div>
          <div className="hidden gap-2 sm:grid sm:grid-cols-2">
            {OBSERVATION_TYPES.map((type) => {
              const Icon = TYPE_ICONS[type.kind];
              const active = kind === type.kind;
              return (
                <button
                  key={type.kind}
                  type="button"
                  onClick={() => setKind(type.kind)}
                  className={`flex min-w-0 items-start gap-2 overflow-hidden rounded-[10px] border-[1.5px] p-2.5 text-left transition-colors ${
                    active
                      ? "shadow-[0_0_0_1px_currentColor]"
                      : "border-[#e5e8ec] bg-white hover:border-[#d6dae0] hover:bg-[#f6f7f9]"
                  }`}
                  style={
                    active
                      ? {
                          borderColor: type.tint,
                          color: type.tint,
                          background: `${type.tint}12`,
                        }
                      : undefined
                  }
                >
                  <span
                    className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-[8px]"
                    style={{
                      color: type.tint,
                      background: active ? `${type.tint}22` : "#f6f7f9",
                    }}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-semibold text-[#14171c]">
                      {t(type.labelKey)}
                    </span>
                    <span className="mt-0.5 block truncate text-[10.5px] text-[#59616d]">
                      {t(type.descKey)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div
          ref={bodyRef}
          className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto overscroll-contain px-3 py-2.5 sm:gap-3 sm:px-4 sm:py-3"
        >
          <div className="flex items-start gap-2 rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9] px-2.5 py-2 text-[11.5px] text-[#59616d]">
            <MapPinIcon className="mt-0.5 h-3.5 w-3.5 flex-none text-[#2b6ef0]" />
            <span>
              {t("drawings.observation.current_drawing_hint", {
                title: drawingTitle || t("drawings.observation.page"),
                page: (page || 0) + 1,
              })}
            </span>
          </div>

          {form}

          {showSketchToggle && (
            <label className="flex cursor-pointer items-start gap-2.5 rounded-[10px] border border-[#e5e8ec] bg-white px-3 py-2.5">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-[#d6dae0] text-[#2b6ef0] focus:ring-[#2b6ef0]"
                checked={wantSketch}
                onChange={(e) => setWantSketch(e.target.checked)}
              />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#14171c]">
                  <PencilSquareIcon className="h-4 w-4 text-[#2b6ef0]" />
                  {t("drawings.observation.sketch.toggle")}
                </span>
                <span className="mt-0.5 block text-[11.5px] text-[#59616d]">
                  {t("drawings.observation.sketch.toggle_hint")}
                </span>
              </span>
            </label>
          )}
        </div>

        <div className="sticky bottom-0 flex flex-shrink-0 justify-end gap-2 border-t border-[#e5e8ec] bg-white px-3 py-2.5 pb-[calc(0.65rem+env(safe-area-inset-bottom,0px))] sm:px-4 sm:py-3 sm:pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-[9px] border border-[#e5e8ec] bg-white px-3 text-[13px] font-semibold text-[#14171c] hover:bg-[#f6f7f9] sm:h-[34px]"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={!valid}
            onClick={handleSubmit}
            className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-[#2b6ef0] bg-[#2b6ef0] px-3 text-[13px] font-semibold text-white shadow-[0_1px_2px_rgba(43,110,240,0.35)] hover:bg-[#2462db] disabled:cursor-default disabled:opacity-45 sm:h-[34px] sm:flex-none"
          >
            <MapPinIcon className="h-4 w-4" />
            {t("drawings.observation.place_on_drawing")}
          </button>
        </div>
      </div>
    </PMDialog>

    {/* Sibling dialog (not nested in PMDialog) — shared type picker with search + HSE details */}
    <EnhancedChecklistSelector
      open={typeSelectorOpen}
      onClose={() => setTypeSelectorOpen(false)}
      showEmptyOption={false}
      showTitleField
      autoTitleFromType
      defaultTitle={checklistTitle}
      onSelect={(config) => {
        if (config?.mode !== "type" || !config?.checklist_type_id) {
          return;
        }
        setSelectedTypeId(String(config.checklist_type_id));
        setSelectedTypeTitle(config.checklist_type_title || "");
        setSelectedItemIds(
          Array.isArray(config.selected_item_ids) ? config.selected_item_ids : []
        );
        setChecklistTitle(config.title || config.checklist_type_title || "");
      }}
    />
    </>
  );
};
